import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Fingerprint, ShieldCheck, BadgeCheck } from "lucide-react";
import { TIER_LABEL, tierBadgeClass } from "@/lib/tiers";

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/share/public/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "This shared standing is unavailable.");
      }
    })();
  }, [token]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen paper-texture flex flex-col items-center justify-center p-6" data-testid="share-view-page">
      <div className="flex items-center gap-2 mb-8">
        <Fingerprint className="text-[#1B4D3E]" size={24} strokeWidth={1.6} />
        <span className="font-serif-dl text-2xl font-semibold tracking-tight">Dap Ledger</span>
      </div>

      {error ? (
        <div data-testid="share-error" className="bg-white border border-[#DEDBD3] rounded-2xl p-10 text-center max-w-md">
          <p className="font-serif-dl text-2xl text-[#9C3A3A]">Link unavailable</p>
          <p className="text-[#5C5A54] mt-2">{error}</p>
        </div>
      ) : !data ? (
        <p className="text-[#5C5A54] font-serif-dl text-xl">Loading credential…</p>
      ) : (
        <div className="bg-white border border-[#DEDBD3] rounded-2xl p-8 sm:p-10 w-full max-w-md shadow-sm animate-fade-up">
          <div className="flex items-center justify-between">
            <div>
              <p className="overline text-[#E07A5F]">Honor Credential</p>
              <h1 className="font-serif-dl text-3xl tracking-tight mt-1" data-testid="share-name">{data.name}</h1>
            </div>
            <span className={`overline px-2.5 py-1 rounded border flex items-center gap-1 ${tierBadgeClass(data.verification_tier)}`}>
              {data.verification_tier === "license" && <BadgeCheck size={12} />}
              {TIER_LABEL[data.verification_tier] || "Unverified"}
            </span>
          </div>

          <div className="mt-8 bg-[#1B4D3E] text-white rounded-xl p-6 text-center">
            <div className="flex items-center justify-center gap-1 text-white/80">
              <ShieldCheck size={16} />
              <span className="overline">Honor Standing</span>
            </div>
            <p className="font-serif-dl text-6xl leading-none mt-3" data-testid="share-score">{data.honor_score}%</p>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="font-serif-dl text-3xl text-[#1A1A1A]">{data.total_sealed}</p>
              <p className="overline text-[#5C5A54] mt-1">Sealed</p>
            </div>
            <div>
              <p className="font-serif-dl text-3xl text-[#2E6B4F]">{data.honored}</p>
              <p className="overline text-[#5C5A54] mt-1">Honored</p>
            </div>
            <div>
              <p className="font-serif-dl text-3xl text-[#1A1A1A]">{data.account_age_days}d</p>
              <p className="overline text-[#5C5A54] mt-1">Account age</p>
            </div>
          </div>

          <div className="mt-8 border-t border-[#DEDBD3] pt-4 text-center">
            <p className="text-sm text-[#5C5A54]">
              This standing was shared directly by <b>{data.shared_by}</b> and verified by Dap Ledger.
            </p>
            <p className="text-xs text-[#A3A095] mt-2 font-mono-dl" data-testid="share-live-timestamp">
              Live at {now.toLocaleString()}
            </p>
            <p className="text-[10px] text-[#A3A095] mt-1">
              A screenshot can't be trusted — only this live page can.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
